import * as THREE from "three";
import * as BufferGeometryUtils from 'three/addons/utils/BufferGeometryUtils.js';
import { ConvexGeometry } from 'three/addons/geometries/ConvexGeometry.js';
import { Text } from '@react-three/drei';
import { Point, Points } from '../shape/Point';
import { TubeCurve } from '../shape/Spline';
import React, { memo, useState, useEffect, useRef } from 'react';
import { drawIntersectionPoints,getContours } from './utils';
import { surfaceFromInwardOutwardPoints, splitSurface } from "../shape/utils/surface";
import { surfaceSurfaceIntersection } from "../shape/utils/intersection";
import { offsetPointsWithBufferGeometryNormal } from "../shape/utils/points";

import { 
  calcCentroid,
  calcCirclePoints, 
  calculateCurveCenter, 
  calculateCurveLength, 
  getContourPointsCircumAll,
  getWrapPoints,
  offsetCurve, 
  intersectionCurvePlane,
  calculateIntersectionPoints,
  lineSegmentsIntersect,
  findClosestToZeroY,
  calcIntersectionMedialCurves,
  findClosestPointOnPoints,
  remapCurve,
  alignSeamPoints,
  makeCurveClockwise,
  sortPoints,
  createLoftSurfaceFrom2Curves,
  createLoftSurfaceFromCurves,
  calculateTweenCurve
} from '../shape/utils';
import { StepElement_VolumeElementPurpose } from "opencascade.js";



/**
 * Trim mesh with 3D projected trim points
 */
export const MTrim = memo(({
  oriMeshSocket,
  ptTrim3D,
  bottomPoints,
  curveWidth,
  curveAngle,
  curveR,
  curveSmooth,
  curveRange,
  callback0,
  callback1,
  callback2,
  color,
  colorHigh,
  colorSub
}) => {
  const objects = [];
  const [objectsSub, setObjectsSub] = useState([]);
  var key = 0;
  const div = 50; //50;
  const angleStep = 5;
  const segmentsWidth = div;
  const segmentsHeight = div;
  
  useEffect(() => {
    console.log("MTrim Local");
    const meshSocket = oriMeshSocket.clone();
    meshSocket.geometry = oriMeshSocket.geometry.clone(); // for deepcopy

    // // This is mesh based trim method
    // // get trim points from 3D projected points
    // const dividedPoints = new THREE.CatmullRomCurve3(ptTrim3D, true).getSpacedPoints(150);
    // const inwardTrimPoints = offsetPointsWithBufferGeometryNormal(dividedPoints, meshSocket.geometry, -5);
    // const outwardTrimPoints = offsetPointsWithBufferGeometryNormal(dividedPoints, meshSocket.geometry, 5);

    // // show offset trim points
    // // objectsSub.push(<Points key={key} vpoints={inwardTrimPoints} color={colorHigh} size={1} />);
    // // key += 1;
    // // objectsSub.push(<Points key={key} vpoints={outwardTrimPoints} color={colorHigh} size={1} />);
    // // key += 1;

    // // get trim surface
    // const trimSurfaceGeometry = surfaceFromInwardOutwardPoints(inwardTrimPoints, outwardTrimPoints);
    // const trimSurface = new THREE.Mesh(trimSurfaceGeometry, new THREE.MeshPhongMaterial({color: colorHigh, side: THREE.DoubleSide, wireframe: true}));

    // // // show trim surface
    // // objectsSub.push(<primitive key={key} object={trimSurface} />);
    // // key += 1;

    // // get intersection points between mesh socket and trim surface
    // //const [intersectionPoints, intersectionFaceIndices, intersectionFaceVertexIndices] = surfaceSurfaceIntersection(meshSocket.geometry, trimSurface.geometry);

    // // get intersection points between mesh socket and trim surface, and trim mesh socket
    // let [intersectionPoints, trimSocketGeometry, lengthNewVertices] = splitSurface(meshSocket.geometry, trimSurface.geometry);
    // //trimSocketGeometry = BufferGeometryUtils.mergeVertices(trimSocketGeometry, 1);
    // //trimSocketGeometry.attributes.normal.needsUpdate = true;
    // //trimSocketGeometry.index.needsUpdate = true;
    // trimSocketGeometry.computeVertexNormals();
    
    // // show intersection points
    // meshSocket.material = new THREE.MeshPhongMaterial({color: colorHigh, side: THREE.DoubleSide, wireframe: true});
    // // objectsSub.push(<Points key={key} vpoints={intersectionPoints} color={colorHigh} size={1} />);
    // // objectsSub.push(<primitive key={key} object={meshSocket} />);

    // const trimSocket = new THREE.Mesh(trimSocketGeometry, new THREE.MeshPhongMaterial({color: colorHigh, side: THREE.DoubleSide, wireframe: true}));
    // objectsSub.push(<primitive key={key} object={trimSocket} />);
    // key += 1;

    // callback0(trimSocket);
    // callback1(intersectionPoints); // intersection points, top edge points
    // callback2(null); // bottom edge points
    // setObjectsSub(objectsSub);














    // This is loft based trim method
    const bottomCenter = calcCentroid(bottomPoints);
    const trimMinY = bottomCenter.y + 2;
    const bbox = new THREE.Box3().setFromObject(meshSocket);
    const trimMaxY = bbox.max.y - 20;

    // detect vertices to delete if it is lower that trimY
    const deleteIdx = [];
    for (let i = 0; i < meshSocket.geometry.attributes.position.array.length; i+=3) {
      if (meshSocket.geometry.attributes.position.array[i+1] > trimMaxY) {
        deleteIdx.push(i/3);
      }
    }

    // delete indices
    const oriIndices = meshSocket.geometry.index.array;
    const newIndices = [];
    for (let i = 0; i < oriIndices.length; i+=3) {
      if (deleteIdx.includes(oriIndices[i]) || deleteIdx.includes(oriIndices[i+1]) || deleteIdx.includes(oriIndices[i+2])) {
        continue;
      } else {
        newIndices.push(oriIndices[i], oriIndices[i+1], oriIndices[i+2]);
      }
    }

    meshSocket.geometry.setIndex(newIndices);
    //meshSocket.geometry = BufferGeometryUtils.mergeVertices(meshSocket.geometry, 1);
    meshSocket.geometry.attributes.normal.needsUpdate = true;
    meshSocket.geometry.index.needsUpdate = true;
    meshSocket.geometry.computeVertexNormals();

    // objectsSub.push(<primitive key={key} object={meshSocket} />);

    // get warp points of mesh socket
    const [warpPointsAll, refPoints] = getWrapPoints(meshSocket, bottomCenter, 0, 0, angleStep, trimMinY, false);
    // get intersection points of trim curve and plane
    const intersectionCurve = new THREE.CatmullRomCurve3(new THREE.CatmullRomCurve3(ptTrim3D, true).getSpacedPoints(div));
    const intersectionPointsAll = calculateIntersectionPoints(intersectionCurve, angleStep, false, bottomCenter);
    const intersectionBottomPointsAll = calculateIntersectionPoints(new THREE.CatmullRomCurve3(bottomPoints), angleStep, true, bottomCenter);
    // sort warp points
    let warpPointsAllSorted = [];
    // const warpPointsBottom = [];
    // // get bottom warp points to sort warp points
    // warpPointsAll.map((warpPoints, i) => {
    //   // if first point y is upper than last point y, then reverse
    //   if (warpPoints[0].y > warpPoints[warpPoints.length-1].y) {
    //     warpPoints.reverse();
    //   }
    //   warpPointsBottom.push(warpPoints[0]);
    // });

    //objectsSub.push(<Points key={key} vpoints={[refPoints[0]]} color={colorHigh} size={2} />);



    // for (let i = 0; i < intersectionBottomPointsAll.length; i++) {
    //   const refPoint = intersectionBottomPointsAll[i]
    //   const closestPoint = findClosestPointOnPoints(warpPointsBottom, refPoint);

    //   warpPointsAllSorted.push(warpPointsAll[closestPoint.idx]);
    // }
    warpPointsAllSorted = warpPointsAll;

    var warpPointsTrimAll = [];
    for (let i = 0; i < intersectionPointsAll.length; i++) {
      // if some warp point is upper than intersection point, then trim
      const intersectionPoint = intersectionPointsAll[i];
      const warpPoints = warpPointsAllSorted[i];
      const warpPointsTrim = warpPoints.filter((warpPoint) => {
        return warpPoint.y < intersectionPoint.y-1; // 1 is margin
      });
      warpPointsTrim.push(intersectionPoint); // add intersection point
      let points = new THREE.CatmullRomCurve3(warpPointsTrim).getSpacedPoints(div);
      points = new THREE.CatmullRomCurve3(points).getSpacedPoints(div);
      warpPointsTrimAll.push(points);
    }

    var weftPointsAll = [];
    // get weft points from warp points
    for (let i = 0; i < div+1; i++) {
      let weftPoints = [];
      warpPointsTrimAll.map((warpPoints) => {
        weftPoints.push(warpPoints[i]);
      });
      weftPoints.push(weftPoints[0]) // close curve
      //weftPoints = new THREE.CatmullRomCurve3(weftPoints).getSpacedPoints(Math.floor(weftPoints.length/4));
      weftPointsAll.push(weftPoints);
    }

    //objectsSub.push(<Points key={key} vpoints={warpPointsTrimAll[1]} color={colorHigh} size={1} />);

    // show weft points
    // weftPointsAll.map((weftPoints) => {
    //   objectsSub.push(
    //     <TubeCurve
    //       key={key}
    //       vpoints={weftPoints}
    //       radius={0.5}
    //       color={colorHigh}
    //     />
    //   );
    //   key += 1;
    // });

    // warpPointsTrimAll.map((warpPoints, i) => {
    //   if (i == 0) {
    //     objectsSub.push(<TubeCurve 
    //       key={key} 
    //       vpoints={warpPoints} 
    //       radius={0.5}
    //       color={colorHigh}
    //       />
    //     );
    //     key += 1;

    //     objectsSub.push(<Points key={key} vpoints={warpPoints} color={colorSub} size={1} />);
    //     key += 1;
    //   }
    // });


    // Bending ////////////////////////////////////////////////
    // get bending points
    const idxBendingPoints = [];
    const vector0 = new THREE.Vector2(0, -1);
    
    weftPointsAll[weftPointsAll.length-1] = weftPointsAll[weftPointsAll.length-1].map((point, idx) => {
      const vector1 = new THREE.Vector2(point.x, point.z);
      const angle = vector0.angleTo(vector1);
      if (angle > 0 && angle < Math.PI/5) {
        idxBendingPoints.push(idx);
        point.z -= 10;
      }
      return point;
    })

    weftPointsAll[weftPointsAll.length-2] = weftPointsAll[weftPointsAll.length-2].map((point, idx) => {
      const vector1 = new THREE.Vector2(point.x, point.z);
      const angle = vector0.angleTo(vector1);
      if (angle > 0 && angle < Math.PI/5) {
        idxBendingPoints.push(idx);
        point.z -= 5;
      }
      return point;
    })




    ///////////////////////////////////////////////////////////


    // weftPointsAll.map((weftPoints) => {
    //   objectsSub.push(<TubeCurve 
    //     key={key} 
    //     vpoints={weftPoints} 
    //     radius={0.5}
    //     color={colorHigh}
    //     />
    //   );
    //   key += 1;
    // });

    // // show top edge points
    // weftPointsAll[weftPointsAll.length-1].map((point, idx) => {
    //   //const color = (idxBendingPoints.includes(idx)) ? colorSub : colorHigh;
    //   objectsSub.push(<Point
    //     key={key}
    //     vpoint={point}
    //     color={colorHigh}
    //     size={2}
    //   />);
    //   key += 1;
    // });

    

    // loft surface
    const loftSurface = createLoftSurfaceFromCurves(weftPointsAll, segmentsWidth, segmentsHeight, color, true);
    objects.push(<primitive key={key} object={loftSurface} />);
    key += 1;


    // // Bending ///////////////////////////////////////////////////////////////////////
    // // get bending real wall points
    // const vector0 = new THREE.Vector2(0, -1);

    // var vertices = loftSurface.geometry.attributes.position.array;
    // for (let i = 0; i < vertices.length; i+=3) {
    //   if (i % 1000 === 0) console.log(i, vertices.length);
    //   var vertex = new THREE.Vector3(vertices[i], vertices[i+1], vertices[i+2]);
    //   if (vertex.y < -30) {
    //     continue; // to speed up
    //   }

    //   // detect bending area interms of angle
    //   const vector1 = new THREE.Vector2(vertex.x, vertex.z);
    //   const angle = vector0.angleTo(vector1);
    //   if ( (angle > 0 && angle < Math.PI*curveRange/180) == false ) {
    //     continue
    //   }
    //   //const ratioAngle = 1- (Math.abs(angle) / (Math.PI*curveRange/180));
    //   const ratioAngle = 1
      

    //   // detect bending area interms of distance
    //   const closestPointToUpperEdge = findClosestPointOnPoints(weftPointsAll[weftPointsAll.length-1], vertex); // upper edge points
    //   const distance = vertex.distanceTo(closestPointToUpperEdge.point);
    //   const ratioDistance = 1 - (distance / curveWidth);
    //   if (distance < curveWidth) {
    //     // move point
    //     vertices[i+2] -= curveWidth * ratioAngle * ratioDistance;      
    //   }
    // }
    
    // weftPointsAll[weftPointsAll.length-1] = weftPointsAll[weftPointsAll.length-1].map((point, idx) => {
    //   const vector1 = new THREE.Vector2(point.x, point.z);
    //   const angle = vector0.angleTo(vector1);
    //   if (angle > 0 && angle < Math.PI/5) {
    //     idxBendingPoints.push(idx);
    //     point.z -= 10;
    //   }
    //   return point;

    /////////////////////////////////////////////////////////////////////////////////




    // get bottom and top edge vertices
    var loftPosition = loftSurface.geometry.attributes.position.array;
    var upperEdgeVertices = [];
    var lowerEdgeVertices = [];
    for (let j = 0; j < loftPosition.length; j+=3) {
      if (j < 3*(segmentsWidth+1)) {
        lowerEdgeVertices.push(new THREE.Vector3(loftPosition[j], loftPosition[j+1], loftPosition[j+2]));
      } else if (j >= 3*(segmentsWidth+1)*(segmentsHeight)) {
        upperEdgeVertices.push(new THREE.Vector3(loftPosition[j], loftPosition[j+1], loftPosition[j+2]));
      }
    }



    // bottom surface
    //// get original vertices and update it
    const centerBottom = calcCentroid(lowerEdgeVertices);
    const oldVertices = loftSurface.geometry.attributes.position.array;
    const newVertices = [...oldVertices, centerBottom.x, centerBottom.y, centerBottom.z];


    // // 同じ頂点座標があるかどうか調べる
    // var vertexIndex = {};
    // for (let i = 0; i < newVertices.length; i+=3) {
    //   const x = newVertices[i];
    //   const y = newVertices[i+1];
    //   const z = newVertices[i+2];
    //   const key = x.toString() + y.toString() + z.toString();
    //   if (vertexIndex[key] === undefined) {
    //     vertexIndex[key] = [i];
    //   } else {
    //     vertexIndex[key].push(i);
    //   }
    // }

    // const duplicateIdx0 = [];
    // const duplicateIdx1 = [];
    // Object.keys(vertexIndex).map((key, i) => {
    //   const v = vertexIndex[key];
    //   if (v.length > 1) {
    //     duplicateIdx0.push(v[0]);
    //     duplicateIdx1.push(v[1]);
    //   }
    // })

    // //remove duplicate vertices
    // const newVertices2 = [];
    // for (let i = 0; i < newVertices.length; i+=3) {
    //   if (duplicateIdx0.includes(i) || duplicateIdx0.includes(i+1) || duplicateIdx0.includes(i+2)) {
    //     continue;
    //   } else {
    //     newVertices2.push(newVertices[i], newVertices[i+1], newVertices[i+2]);
    //   }
    // }




    

    const newAttribute = new THREE.Float32BufferAttribute(newVertices, 3);
    loftSurface.geometry.setAttribute('position', newAttribute);
    
    // get original faces and update it
    const oldFaces = loftSurface.geometry.index.array;
    var newFaces = [...oldFaces];

    const lastVertexIndex = loftSurface.geometry.attributes.position.count - 1;

    for (let i = 0; i < segmentsWidth; i+=1) {
      newFaces.push(i+1, i, lastVertexIndex);
    }

    /*
    // remove duplicate vertices
    // newFaces.map((face, i) => {
    //   console.log(i)
    //   if (duplicateIdx0.includes(face)) {
        
    //     // newFaces[i] = duplicateIdx1[duplicateIdx0.indexOf(face)];
    //   }
    // });

    const newFaces2 = [];
    // for (let i = 0; i < newFaces.length; i+=3) {
    //   if (duplicateIdx0.includes(newFaces[i])) {
    //     console.log(duplicateIdx0.includes(newFaces[i]))
    //     //newFaces2.push(duplicateIdx1[duplicateIdx0.indexOf(newFaces[i])], newFaces[i+1], newFaces[i+2]);
    //     //newFaces2.push(newFaces[i], newFaces[i+1], newFaces[i+2]);
    //   } else if (duplicateIdx0.includes(newFaces[i+1])){
    //     //newFaces2.push(newFaces[i], duplicateIdx1[duplicateIdx0.indexOf(newFaces[i+1])], newFaces[i+2]);
    //   } else if (duplicateIdx0.includes(newFaces[i+2])) {
    //     //newFaces2.push(newFaces[i], newFaces[i+1], duplicateIdx1[duplicateIdx0.indexOf(newFaces[i+2])]);
    //   } else {
    //     newFaces2.push(newFaces[i], newFaces[i+1], newFaces[i+2]);
    //   }
    // }
    */



    /*
    for (let i = 0; i < newFaces.length; i++) {
      //if (duplicateIdx1.includes(newFaces[i])) {
      if (duplicateIdx0.includes(newFaces[i])) {
        //const temp = duplicateIdx1[duplicateIdx0.indexOf(newFaces[i])];
        
        //newFaces[i] = duplicateIdx1[duplicateIdx0.indexOf(newFaces[i])];
        
        // console.log(newFaces[i])

        // const vertex = [newVertices[newFaces[i]*3], newVertices[newFaces[i]*3+1], newVertices[newFaces[i]*3+2]];
        // console.log(vertex)
        // console.log(temp)
        // const vertex2 = [
        //   newVertices[temp*3], 
        //   newVertices[newFaces[i]*3+1], 
        //   newVertices[newFaces[i]*3+2],
        // ]
        // console.log(vertex2)
      }
    }
    */


    loftSurface.geometry.setIndex(newFaces);
    loftSurface.geometry.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(newVertices.length), 3)); // need to add this line to update normals if you add vertices
    loftSurface.geometry = BufferGeometryUtils.mergeVertices(loftSurface.geometry, 2);
    loftSurface.geometry.attributes.normal.needsUpdate = true;
    loftSurface.geometry.index.needsUpdate = true;
    loftSurface.geometry.computeVertexNormals();

    callback0(loftSurface); // loft mesh
    callback1(upperEdgeVertices) // top edge points
    callback2(lowerEdgeVertices) // bottom edge points

  }, []);

  return (
    <>
      {objectsSub}
    </>
  )

  //return null;
});